import { useState, useEffect } from "react";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";
import PeriodToggle from "./PeriodToggle";
import { CalculationOutput } from "../types";
import { MoreHorizontal, LogOut, User, FileText, FileSpreadsheet, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PaywallModal from "./PaywallModal";
import PremiumBanner from "./PremiumBanner";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header({
  active,
  onChangeActive,
  periodMode,
  onChangePeriodMode,
  onDuplicate,
  output,
  onLoadExample,
  onReset,
  onSaveSimulation,
  onOpenSimulations,
  simulationsCount,
  onDownloadPDF,
  onDownloadCSV,
  showPremiumBanner = false,
  onDismissPremiumBanner,
  showStickySummary = true,
}: {
  active: "A" | "B";
  onChangeActive: (s: "A" | "B") => void;
  periodMode: PeriodMode;
  onChangePeriodMode: (m: PeriodMode) => void;
  onDuplicate: () => void;
  output: CalculationOutput | null;
  onLoadExample: () => void;
  onReset: () => void;
  onSaveSimulation: () => void;
  simulationsCount: number;
  onDownloadPDF?: () => void;
  onDownloadCSV?: () => void;
  showPremiumBanner?: boolean;
  onDismissPremiumBanner?: () => void;
  showStickySummary?: boolean;
  onOpenSimulations?: () => void;
}) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const isPremium = !!user;
  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuOpen && !(e.target as HTMLElement).closest('.more-menu-container')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menuOpen]);

  const bruto = (output as any)?.brutoFacturado ?? 0;
  const caja = (output as any)?.cajaPreviaRenta ?? 0;
  const rendimiento = (output as any)?.rendimientoNeto ?? 0;
  const neto = (output as any)?.netoFinalTrasRenta ?? output?.netoFiscalAnual ?? 0;

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        backgroundColor: "rgba(255,255,255,0.98)",
        borderBottom: "1px solid var(--border)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div id="google_translate_element" style={{ display: "none" }} />
      <div
        className="header-inner"
        style={{
          maxWidth: "1536px",
          margin: "0 auto",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <style>{`
          @media (min-width: 640px) {
            .header-inner { padding: 16px 32px !important; }
          }
          @media (min-width: 1024px) {
            .header-inner { padding: 16px 48px !important; }
          }
          .auth-cta-group {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .auth-cta {
            appearance: none;
            border-radius: 999px;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.15s ease;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;
          }
          .auth-cta:focus-visible {
            outline: 2px solid var(--brand);
            outline-offset: 2px;
          }
          .auth-cta-primary {
            background: var(--brand);
            border: none;
            color: white;
          }
          .auth-cta-primary:hover {
            background: var(--brand-ink);
          }
          .auth-cta-ghost {
            background: transparent;
            border: 1px solid var(--border);
            color: var(--text);
          }
          .auth-cta-ghost:hover {
            border-color: var(--brand);
            color: var(--brand-ink);
            background: rgba(59, 130, 246, 0.06);
          }
          .menu-auth {
            display: none;
          }
          @media (max-width: 768px) {
            .header-inner {
              padding: 10px 12px !important;
              flex-wrap: nowrap;
              gap: 10px;
            }
            .header-left {
              flex: 0 0 auto;
              min-width: 0;
              gap: 10px !important;
            }
            .header-center {
              display: none !important;
            }
            .header-actions.free {
              flex: 0 0 auto;
              justify-content: flex-end;
              flex-wrap: nowrap;
            }
            .header-actions.premium {
              flex: 0 0 auto;
              gap: 8px;
            }
            .sticky-summary {
              display: none !important;
            }
            .scenario-toggle {
              display: none !important;
            }
            .lang-switch {
              display: none !important;
            }
            .profile-button,
            .signout-button {
              display: none !important;
            }
            .auth-cta-group {
              flex-direction: row;
              align-items: center;
              width: auto;
              flex-wrap: nowrap;
            }
            .header-actions.free .auth-cta-group {
              display: none;
            }
            .auth-cta {
              justify-content: center;
              padding: 6px 10px;
              font-size: 12px;
              flex: 0 0 auto;
            }
            .auth-cta-full {
              display: none;
            }
            .auth-cta-short {
              display: inline;
            }
            .user-pill {
              display: none !important;
            }
            .period-toggle {
              transform: scale(0.92);
              transform-origin: center;
            }
            .ptab {
              padding: 6px 10px;
              font-size: 12px;
            }
            .period-label {
              display: none;
            }
            .menu-auth {
              display: inline-flex;
            }
          }
          @media (min-width: 769px) {
            .signout-button {
              display: none !important;
            }
          }
        `}</style>
        {/* ZONA IZQUIERDA: Identidad y Contexto */}
        <div className="header-left" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src="/logo.png"
              alt="Autónomo Claro"
              style={{
                height: "32px",
                width: "auto",
                maxWidth: "160px",
                display: "block",
                objectFit: "contain",
                flexShrink: 0,
              }}
              loading="lazy"
            />
          </div>

          <div className="lang-switch">
            <LanguageSwitcher />
          </div>

          {/* Selector de Escenarios - Segmented Control - Solo en Premium */}
          {isPremium && (
            <div
              className="scenario-toggle"
              style={{
                display: "flex",
                gap: "4px",
                backgroundColor: "#f3f4f6",
                borderRadius: "12px",
                padding: "4px",
              }}
            >
              {["A", "B"].map((s) => (
                <button
                  key={s}
                  onClick={() => onChangeActive(s as "A" | "B")}
                  style={{
                    appearance: "none",
                    border: "none",
                    backgroundColor: active === s ? "#fff" : "transparent",
                    color: active === s ? "var(--text)" : "var(--muted)",
                    fontWeight: 600,
                    fontSize: "13px",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: active === s ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ display: "inline" }}>Escenario {s}</span>
                </button>
              ))}
            </div>
          )}

          {/* Resumen sticky al hacer scroll */}
          {scrolled && output && showStickySummary && (
            <div
              className="sticky-summary"
              style={{
                display: "flex",
                gap: "16px",
                marginLeft: "16px",
                opacity: scrolled ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Bruto
                </div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#3b82f6" }}>
                  {fmtEUR(bruto, periodMode)}{suffix(periodMode)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Rendim. neto
                </div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#10b981" }}>
                {fmtEUR(rendimiento, periodMode)}{suffix(periodMode)}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Neto tras IRPF
              </div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#8b5cf6" }}>
                {fmtEUR(neto, periodMode)}{suffix(periodMode)}
              </div>
            </div>
            </div>
          )}
        </div>

        {/* ZONA CENTRAL: Control de Vista */}
        <div className="header-center" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isPremium && <PeriodToggle value={periodMode} onChange={onChangePeriodMode} />}
        </div>

        {/* ZONA DERECHA: Acciones */}
        <div
          className={`header-actions ${isPremium ? "premium" : "free"}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {!isPremium ? (
            <div className="auth-cta-group">
              <button
                type="button"
                className="auth-cta auth-cta-ghost"
                onClick={() => navigate('/login')}
                aria-label="Iniciar sesión"
              >
                <span className="auth-cta-full">Iniciar sesión</span>
                <span className="auth-cta-short" style={{ display: "none" }}>Entrar</span>
              </button>
              <button
                type="button"
                className="auth-cta auth-cta-primary"
                onClick={() => navigate('/signup')}
                aria-label="Alta Premium"
              >
                <User size={16} />
                <span className="auth-cta-full">Alta Premium</span>
                <span className="auth-cta-short" style={{ display: "none" }}>Alta</span>
              </button>
            </div>
          ) : (
            <>
              <button
                className="profile-button"
                onClick={() => navigate('/profile')}
                style={{
                  appearance: "none",
                  background: "none",
                  border: "1px solid var(--border)",
                  borderRadius: "999px",
                  padding: "6px 10px",
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  color: "var(--muted)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                  e.currentTarget.style.color = "var(--brand-ink)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--muted)";
                }}
                title="Mi perfil"
              >
                <Settings size={16} />
              </button>

              <div
                className="user-pill"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 12px",
                  backgroundColor: "var(--brand-soft)",
                  borderRadius: "999px",
                }}
              >
                <User size={14} />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--brand-ink)",
                  }}
                >
                  {user?.email}
                </span>
              </div>

              <button
                className="signout-button"
                onClick={handleSignOut}
                style={{
                  appearance: "none",
                  background: "none",
                  border: "1px solid var(--border)",
                  borderRadius: "999px",
                  padding: "6px 10px",
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  color: "var(--muted)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#fee2e2";
                  e.currentTarget.style.color = "#ef4444";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--muted)";
                }}
                title="Cerrar sesión"
              >
                <LogOut size={16} />
              </button>
            </>
          )}

          {/* Menú Más Opciones */}
          <div className="more-menu-container" style={{ position: "relative" }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                appearance: "none",
                background: menuOpen ? "var(--brand-soft)" : "none",
                border: "1px solid var(--border)",
                borderRadius: "999px",
                padding: "8px 10px",
                fontSize: "13px",
                fontWeight: 600,
                color: menuOpen ? "var(--brand-ink)" : "var(--muted)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                if (!menuOpen) {
                  e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                  e.currentTarget.style.color = "var(--brand-ink)";
                }
              }}
              onMouseLeave={(e) => {
                if (!menuOpen) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--muted)";
                }
              }}
            >
              <MoreHorizontal size={18} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  backgroundColor: "#fff",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  padding: "8px",
                  minWidth: "200px",
                  zIndex: 1000,
                }}
              >
                {!isPremium && (
                  <>
                    <button
                      className="menu-auth"
                      onClick={() => {
                        navigate('/login');
                        setMenuOpen(false);
                      }}
                      style={{
                        appearance: "none",
                        background: "white",
                        border: "1px solid var(--border)",
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "var(--text)",
                        cursor: "pointer",
                        textAlign: "left",
                        borderRadius: "10px",
                        transition: "all 0.15s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                        e.currentTarget.style.color = "var(--brand-ink)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.color = "var(--text)";
                      }}
                    >
                      <span>Entrar</span>
                      <span style={{ fontSize: "11px", color: "var(--muted)" }}>Acceso</span>
                    </button>
                    <button
                      className="menu-auth"
                      onClick={() => {
                        navigate('/signup');
                        setMenuOpen(false);
                      }}
                      style={{
                        appearance: "none",
                        background: "var(--brand)",
                        border: "none",
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "white",
                        cursor: "pointer",
                        textAlign: "left",
                        borderRadius: "10px",
                        transition: "all 0.15s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 6px 16px rgba(37, 99, 235, 0.25)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--brand-ink)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--brand)";
                      }}
                    >
                      <span>Alta</span>
                      <span style={{ fontSize: "11px", opacity: 0.85 }}>Premium</span>
                    </button>
                  </>
                )}
                {isPremium && (
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setMenuOpen(false);
                    }}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--text)",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <Settings size={16} />
                    Mi perfil
                  </button>
                )}
                <button
                  onClick={() => {
                    onLoadExample();
                    setMenuOpen(false);
                  }}
                  style={{
                    appearance: "none",
                    background: "none",
                    border: "none",
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--text)",
                    cursor: "pointer",
                    textAlign: "left",
                    borderRadius: "8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  📋 Cargar Ejemplo
                </button>
                <button
                  onClick={() => {
                    onDuplicate();
                    setMenuOpen(false);
                  }}
                  style={{
                    appearance: "none",
                    background: "none",
                    border: "none",
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--text)",
                    cursor: "pointer",
                    textAlign: "left",
                    borderRadius: "8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  🔁 Duplicar Escenario
                </button>
                {isPremium && (
                  <div
                    className="period-toggle-menu"
                    style={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      background: "#f8fafc",
                      border: "1px solid var(--border)",
                      margin: "4px 0",
                    }}
                  >
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted)", marginBottom: "8px" }}>
                      Vista
                    </div>
                    <PeriodToggle value={periodMode} onChange={onChangePeriodMode} />
                  </div>
                )}
                {isPremium && onOpenSimulations && (
                  <button
                    onClick={() => {
                      onOpenSimulations();
                      setMenuOpen(false);
                    }}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--text)",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span>📁 Mis simulaciones</span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--muted)",
                        padding: "2px 8px",
                        backgroundColor: "var(--bg)",
                        borderRadius: "999px",
                        fontWeight: 600,
                      }}
                    >
                      {simulationsCount}
                    </span>
                  </button>
                )}
                {isPremium && (
                  <button
                    onClick={() => {
                      onSaveSimulation();
                      setMenuOpen(false);
                    }}
                    disabled={!output}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: output ? "var(--text)" : "var(--muted)",
                      cursor: output ? "pointer" : "not-allowed",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (output) {
                        e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    💾 Guardar simulación
                  </button>
                )}
                {onDownloadPDF && (
                  <button
                    onClick={() => {
                      onDownloadPDF();
                      setMenuOpen(false);
                    }}
                    disabled={!output}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: output ? "var(--text)" : "var(--muted)",
                      cursor: output ? "pointer" : "not-allowed",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (output) {
                        e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <FileText size={16} />
                    Descargar PDF
                  </button>
                )}
                {onDownloadCSV && (
                  <button
                    onClick={() => {
                      onDownloadCSV();
                      setMenuOpen(false);
                    }}
                    disabled={!output}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: output ? "var(--text)" : "var(--muted)",
                      cursor: output ? "pointer" : "not-allowed",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (output) {
                        e.currentTarget.style.backgroundColor = "var(--brand-soft)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <FileSpreadsheet size={16} />
                    Descargar CSV
                  </button>
                )}
                {isPremium && (
                  <button
                    onClick={async () => {
                      await handleSignOut();
                      setMenuOpen(false);
                    }}
                    style={{
                      appearance: "none",
                      background: "none",
                      border: "none",
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#ef4444",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: "8px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fee2e2";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <LogOut size={16} />
                    Cerrar sesión
                  </button>
                )}
                <button
                  onClick={() => {
                    onReset();
                    setMenuOpen(false);
                  }}
                  style={{
                    appearance: "none",
                    background: "none",
                    border: "none",
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#ef4444",
                    cursor: "pointer",
                    textAlign: "left",
                    borderRadius: "8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fee2e2";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  🗑️ Limpiar Datos
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showPremiumBanner && (
        <div style={{ borderTop: "1px solid #fbbf24" }}>
          <PremiumBanner variant="hero" onClose={onDismissPremiumBanner} />
        </div>
      )}
      <PaywallModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="save"
      />
    </header>
  );
}
